import { sonarPlaceType } from '@web-platform/shared-universal-constants/src/redux/state/entities/places/sonar';
import { makeEntityId } from '@web-platform/shared-universal-types-utils/src/utils';
import { nonNullishOrThrow } from '@web-platform/shared-universal-utils-frontend/src/helpers/assertNonNullish';
import { logError } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';

import type { SkyscannerSonarV3Response } from '@skyscanner-internal/sonar-web-http-client';
import type { PlaceBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/placesBookingPanel';
import type { Maybe } from '@web-platform/shared-universal-types-utils/src/utils';

type LegacyPlace = SkyscannerSonarV3Response.LegacyPlace;
type PlaceType = SkyscannerSonarV3Response.PlaceType;

const mapSingleSonarV3Place = (sonarPlace: LegacyPlace): PlaceBookingPanel => ({
  id: nonNullishOrThrow(sonarPlace.altId, 'place.altId'),
  display_code: nonNullishOrThrow(sonarPlace.displayCode, 'place.displayCode'),
  name: nonNullishOrThrow(sonarPlace.name, 'place.name'),
  type: mapSonarPlacesType(nonNullishOrThrow(sonarPlace.placeType, 'place.placeType')),
  ...(sonarPlace.parentAltId
    ? {
        parent_id: sonarPlace.parentAltId,
      }
    : {}),
  ...(sonarPlace.parentEntityId
    ? {
        parent_entity_id: makeEntityId(sonarPlace.parentEntityId.toString()),
      }
    : {}),
  entity_id: makeEntityId(nonNullishOrThrow(sonarPlace.entityId, 'place.entityId').toString()),
});

export const mapSonarV3Places = (sonarPlaces: Maybe<LegacyPlace[]>): PlaceBookingPanel[] => {
  const mappedPlaces: PlaceBookingPanel[] = [];

  sonarPlaces?.forEach((sonarPlace) => {
    try {
      mappedPlaces.push(mapSingleSonarV3Place(sonarPlace));
    } catch (error) {
      logError({
        component: 'mapSonarV3Places',
        message: 'SONARV3: Error mapping places',
        data: JSON.stringify(sonarPlace),
        error,
        stack: error?.stack,
      });
    }
  });

  return mappedPlaces;
};

type UpdateStatusMap = {
  [key in Exclude<PlaceType, 'PLACE_TYPE_UNSPECIFIED'>]: string;
};

const placesTypeMap: UpdateStatusMap = {
  PLACE_TYPE_AIRPORT: 'Airport',
  PLACE_TYPE_CITY: 'City',
  PLACE_TYPE_COUNTRY: 'Country',
} as const;

const mapSonarPlacesType = (placeType?: PlaceType): string => {
  if (!placeType || placeType === sonarPlaceType.PLACE_TYPE_UNSPECIFIED) {
    throw new Error(`Unexpected V3 placeType: ${placeType}`);
  }

  return placesTypeMap[placeType];
};
